/**
 * `/help/roles` — who uses what.
 *
 * The port of the demo's Help "roles" tab (demo line 3934): the six roles in
 * the VIEWING AS picker, what each one owns, the five pages they actually
 * open, and the typical morning that explains the order.
 *
 * Each card carries a "View as this persona" button the demo did not have.
 * The demo's picker and its role guide were separate things; here the guide is
 * the fastest way into a persona, and switching from a card is one click
 * rather than a hunt through the top bar.
 *
 * @see src/versions/v1/modules/help/RolesScene.transform.ts
 * @see docs/logic/help/roles.md
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { HelpAnchor, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { HelpTabs } from './HelpTabs';
import { useOemPersona } from '../../shared/OemPersonaContext';
import { HANDOVER, roleCards } from './RolesScene.transform';

/** Demo line 3934 — the tab standfirst, verbatim. */
const STANDFIRST =
  'The six roles in the VIEWING AS picker, and where each one actually spends the day. ' +
  'Switching persona re-weights the assistant’s suggestions and lands you on that role’s home.';

export function RolesScene() {
  const cards = useMemo(() => roleCards(), []);
  const { personaId, setPersona } = useOemPersona();

  return (
    <SurfacePageLayout width="full">
      <Commentable name="RolesScene">
        <SurfacePageBody className="@container max-w-[1500px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Help"
            title="Who uses what"
            description={STANDFIRST}
            actions={<HelpAnchor doc="help/roles" />}
            tabs={<HelpTabs current="roles" />}
          />

          <Commentable name="RoleCards">
            <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
              {cards.map((card) => {
                const active = personaId === card.personaId;
                return (
                  <section
                    key={card.id}
                    className="flex min-w-0 flex-col gap-2.5 rounded-xl border p-4"
                    style={{
                      borderColor: 'var(--hairline)',
                      background: 'var(--surface-2)',
                      borderTop: `3px solid ${card.colour}`,
                    }}
                  >
                    <header className="flex items-center gap-2.5">
                      <span
                        aria-hidden="true"
                        className="mono inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold"
                        style={{
                          background: `color-mix(in srgb, ${card.colour} 16%, transparent)`,
                          color: card.colour,
                        }}
                      >
                        {card.initials}
                      </span>
                      <span className="min-w-0 flex-1">
                        <b
                          className="block truncate text-[13.5px] font-semibold"
                          style={{ color: 'var(--ink-primary)' }}
                        >
                          {card.name}
                        </b>
                        <small className="block text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
                          {card.org}
                        </small>
                      </span>
                    </header>

                    <p
                      className="text-[11.5px] leading-[1.55]"
                      style={{ color: 'var(--ink-secondary)' }}
                    >
                      {card.what}
                    </p>

                    <ul className="flex flex-col gap-1">
                      {card.pages.map((page) =>
                        page.to ? (
                          <li key={page.title}>
                            <Link
                              to={page.to}
                              className="block rounded-lg border px-2.5 py-1.5 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                              style={{ borderColor: 'var(--hairline)' }}
                            >
                              <b
                                className="block text-[11.5px] font-medium"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {page.title}
                              </b>
                              <span
                                className="block text-[10.5px] leading-snug"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                {page.question}
                              </span>
                            </Link>
                          </li>
                        ) : null,
                      )}
                    </ul>

                    <div
                      className="mt-auto rounded-lg px-2.5 py-2"
                      style={{ background: 'var(--surface-3)' }}
                    >
                      <span
                        className="block text-[10px] font-semibold uppercase tracking-[0.08em]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        A typical morning
                      </span>
                      <span
                        className="mt-0.5 block text-[11px] leading-snug"
                        style={{ color: 'var(--ink-secondary)' }}
                      >
                        {card.day}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPersona(card.personaId)}
                      disabled={active}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] disabled:cursor-default disabled:opacity-60"
                      style={{
                        borderColor: active ? 'var(--accent)' : 'var(--hairline)',
                        color: active ? 'var(--accent)' : 'var(--ink-primary)',
                        background: active ? 'var(--accent-soft)' : 'transparent',
                      }}
                    >
                      <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                      {active ? 'Currently viewing as this persona' : 'View as this persona'}
                    </button>
                  </section>
                );
              })}
            </div>
          </Commentable>

          <Commentable name="RoleHandover">
            <section
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <h2
                className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                How work moves between them
              </h2>
              <ol className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 @2xl:grid-cols-2 @5xl:grid-cols-5">
                {HANDOVER.map((row, i) => (
                  <li key={row.heading}>
                    <span
                      className="mono block text-[9.5px] font-semibold"
                      style={{ color: 'var(--ink-tertiary)' }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <b className="block text-[12px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
                      {row.heading}
                    </b>
                    <span
                      className="mt-0.5 block text-[11px] leading-[1.55]"
                      style={{ color: 'var(--ink-secondary)' }}
                    >
                      {row.body}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
